import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { useIncidentTimeSeries } from '../../services/api/hooks/useIncidentTimeSeries';
import { useFilterStore } from '../filters/filterStore';

const MONTHS = ['janv.', 'févr.', 'mars', 'avril', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};

export const TimeSeriesChart = () => {
  const { scopes, dateRange } = useFilterStore();
  const query = useIncidentTimeSeries({ scopes, dateRange });
  const points = query.data?.points ?? [];

  return (
    <article className="rounded-md border border-zinc-200 bg-white p-6 flex flex-col gap-5">
      <header>
        <h2 className="text-sm font-medium text-zinc-900">Évolution</h2>
        <p className="text-xs text-zinc-500 mt-0.5">Volume de courses et taux d&apos;incident</p>
      </header>

      {query.isLoading ? (
        <div className="h-[260px] rounded-sm bg-zinc-50 skeleton-pulse" />
      ) : points.length === 0 ? (
        <div className="h-[260px] flex flex-col items-center justify-center gap-2 text-center">
          <p className="text-sm text-zinc-900">Aucune donnée sur la période.</p>
          <p className="text-xs text-zinc-500">Ajustez la période pour afficher davantage de résultats.</p>
        </div>
      ) : (
        <div className="h-[260px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="#f4f4f5" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                stroke="#a1a1aa"
                tick={{ fontSize: 11, fontFamily: 'Geist Mono, ui-monospace, monospace' }}
                tickLine={false}
                axisLine={{ stroke: '#e4e4e7' }}
                interval="preserveStartEnd"
                minTickGap={32}
              />
              <YAxis
                yAxisId="rides"
                stroke="#a1a1aa"
                tick={{ fontSize: 11, fontFamily: 'Geist Mono, ui-monospace, monospace' }}
                tickLine={false}
                axisLine={false}
                width={36}
              />
              <YAxis
                yAxisId="incident"
                orientation="right"
                stroke="#a1a1aa"
                tick={{ fontSize: 11, fontFamily: 'Geist Mono, ui-monospace, monospace' }}
                tickLine={false}
                axisLine={false}
                unit=" %"
                width={48}
              />
              <Tooltip
                contentStyle={{
                  background: '#ffffff',
                  border: '1px solid #e4e4e7',
                  borderRadius: 4,
                  fontSize: 12,
                  padding: '8px 12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  fontFamily: 'Geist Mono, ui-monospace, monospace',
                }}
                labelStyle={{ color: '#71717a', fontSize: 11 }}
                labelFormatter={iso => formatDate(iso as string)}
              />
              <Line
                yAxisId="rides"
                type="monotone"
                dataKey="rides"
                name="Courses"
                stroke="#18181b"
                strokeWidth={1.5}
                dot={false}
                activeDot={{ r: 3, fill: '#18181b', stroke: '#ffffff', strokeWidth: 2 }}
              />
              <Line
                yAxisId="incident"
                type="monotone"
                dataKey="incidentRate"
                name="Taux d'incident"
                stroke="#dc2626"
                strokeWidth={1.5}
                strokeDasharray="3 3"
                dot={false}
                activeDot={{ r: 3, fill: '#dc2626', stroke: '#ffffff', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <footer className="flex items-center gap-5 text-xs text-zinc-500">
        <span className="flex items-center gap-1.5">
          <span className="h-px w-3.5 bg-zinc-900" />
          Courses
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="h-px w-3.5"
            style={{
              backgroundImage: 'linear-gradient(to right, #dc2626 50%, transparent 50%)',
              backgroundSize: '4px 1px',
            }}
          />
          Taux d&apos;incident
        </span>
      </footer>
    </article>
  );
};
